import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import classNames from "classnames";
import React from "react";
import { useEffect, useState } from "react";
import * as Icons from "Components/Icons";
import { EmailInputFL, InputFL, ValidationSummary } from "Components/Inputs";
import { Button, Modal, ModalBody, ModalProps, SpinningButton } from "Components/ReactstrapSlim";
import { EnterpriseClient, ILiquidspaceProRequest, LiquidspaceProRequest } from "Services/API/API";
import { useSelector } from "react-redux";
import { emptyStringToUndefined } from "Validation/emptyStringToUndefined";
import { useApiClient } from "Services/API/useApiClient";
import { userSelector } from "Utils/selectors";

import classes from "../Modal.modules.scss"

function getProModel(data: ILiquidspaceProRequest) {
   return {
      fullName: data.fullName,
      emailAddress: data.emailAddress,
      phoneNumber: data.phoneNumber,
      companyName: data.companyName,
      locationsCount: data.locationsCount,
      comment: data.comment
   }
}

type ProRequestForm = ReturnType<typeof getProModel>;

const ProRequestFormSchema: yup.SchemaOf<ProRequestForm> = yup.object({
   fullName: yup.string().fullName(true, "Full name is required").defined(),
   emailAddress: yup.string()
      .required("Email is required")
      .email("Email is not valid")
      .defined(),
   phoneNumber: yup.string().phone("Phone number is not valid"),
   companyName: yup.string().required("Company name is required").defined(),
   locationsCount: yup.number()
      .typeError("Number of locations must be a number")
      .transform(emptyStringToUndefined).required("Number of locations is required")
      .integer("Number of locations must be an integer")
      .min(1, "Number must be between 1 and 9999")
      .max(9999, "Number must be between 1 and 9999").defined(),
   comment: yup.string().optional()
});

export interface ProRequestModalProps extends ModalProps {
   toggle: () => void;
}

const ProRequestModal: React.FC<ProRequestModalProps> = ({ toggle, ...rest }) => {
   const enteripseClient = useApiClient(EnterpriseClient);

   const [showSuccessScreen, setShowSuccessScreen] = useState(false);
   const [isSubmitting, setIsSubmitting] = useState(false);
   const [serverError, setServerError] = useState<Array<[string, string]>>([]);
   const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);

   const user = useSelector(userSelector);

   const formMethods = useForm<ProRequestForm>({
      mode: "onTouched",
      resolver: yupResolver(ProRequestFormSchema),
      defaultValues: {
         fullName: user?.fullName || undefined,
         emailAddress: user?.email || undefined
      }
   })

   const formValues = formMethods.watch();

   const submit = async () => {
      setIsSubmitting(true);
      try {
         const isValid = await formMethods.trigger();
         if (isValid) {
            const requestData = formMethods.getValues();
            const requestResult = await enteripseClient.submitProRequest(new LiquidspaceProRequest({ ...requestData }));

            if (requestResult.success) {
               setShowSuccessScreen(true);
            } else {
               setServerError([["requestDemo", requestResult.errorMessage || "Unexpected server error"]]);
            }
         }
      } catch (err: any) {

      } finally {
         setIsSubmitting(false);
      }
   }

   useEffect(() => {
      ProRequestFormSchema.isValid(formValues).then(isValid => setSubmitButtonDisabled(!isValid));
   }, [formValues.fullName, formValues.emailAddress, formValues.phoneNumber, formValues.companyName, formValues.locationsCount, formValues.comment]);

   return (
      <Modal className={classNames(classes.contactUsModal)} size="lg" trapFocus {...rest}>
         <ModalBody>
            <div className="row">
               <div className={classNames(classes.contactUsHeroSection, "col-12", "col-lg-4")}>
                  <div className={classes.closeButton} role="button" tabIndex={0} aria-label="close" onClick={() => toggle()}><Icons.Close className="icon" color="#FFF" /></div>
                  <div className={classes.caption}>Connect with our team</div>
                  <div className={classes.description}>Let us know how we can help solve for your workplace needs</div>
               </div>
               <div className={classNames(classes.dataSection, "col-12", "col-lg-8")}>
                  <div className={classes.closeButton} role="button" tabIndex={0} aria-label="close" onClick={() => toggle()}><Icons.Close className="icon" /></div>
                  <div className={classes.formSection}>
                     <div className={classNames(classes.successScreen, { "d-none": showSuccessScreen })}>
                        <div>
                           <div>
                              <InputFL control={formMethods.control} name="fullName" placeholder="Full Name" aria-label="full name" bsSize="lg" className={classNames(classes.firstInput, "rfl-48")} readOnly={!!user?.email} />
                              <EmailInputFL control={formMethods.control} name="emailAddress" placeholder="Email Address" bsSize={"lg"} className="rfl-48" autoComplete="off" readOnly={!!user?.fullName} />
                              <InputFL control={formMethods.control} name="phoneNumber" placeholder="Phone (Optional)" bsSize="lg" className="rfl-48" />
                              <InputFL control={formMethods.control} name="companyName" placeholder="Company Name" bsSize="lg" className="rfl-48" />
                              <InputFL control={formMethods.control} name="locationsCount" placeholder="Number of locations" bsSize="lg" className="rfl-48" />
                              <InputFL control={formMethods.control} name="comment" placeholder="Anything else you’d like our team to know?" mobilePlaceholder="Notes (Optional)" type="textarea" bsSize="lg" className={classNames(classes.questionInput, "rfl-48")} />
                           </div>
                           <ValidationSummary errors={formMethods.formState.errors} serverErrors={serverError} />
                        </div>
                        <div className={classNames(classes.formButtonWrapper)}>
                           <SpinningButton color="primary" onClick={submit} isLoading={isSubmitting} disabled={submitButtonDisabled} size="md" fluid="sm">Get in touch</SpinningButton>
                        </div>
                     </div>
                     <div className={classNames(classes.successScreen, { "d-none": !showSuccessScreen })}>
                        <div className={classes.successImg}></div>
                        <div className={classes.successMsg}>Thank you for filling out the form.<br />It has been successfully submitted</div>
                        <div className={classes.buttonWrapper}>
                           <Button className={classes.successBtn} color="primary" onClick={() => toggle()} size="md" fluid="sm">Got it</Button>
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         </ModalBody>
      </Modal>
   );
}

export default ProRequestModal;